import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Calendar, 
  Search, 
  SlidersHorizontal,
  ChevronRight,
  ChevronLeft,
  ChevronsRight,
  ChevronsLeft,
  Newspaper,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import PageHero from '../components/PageHero';
import Announcements from '../components/Announcements';
import SEO from '../components/SEO';
import { getImageUrl } from '../lib/utils';

const News = () => {
  const [news, setNews] = useState([]);
  const [filteredNews, setFilteredNews] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/news/`).then(res => res.json()),
      fetch(`${BACKEND_URL}/api/settings/`).then(res => res.json())
    ])
      .then(([newsData, settingsData]) => {
        setNews(newsData);
        setFilteredNews(newsData);
        setSettings(settingsData);
      })
      .catch(err => console.error('Error fetching data:', err))
      .finally(() => setLoading(false));
  }, [BACKEND_URL]);

  // Get unique categories
  const categories = ['all', ...new Set(news.map(item => item.category).filter(Boolean))];

  // Filter and sort news
  useEffect(() => {
    let result = [...news];

    // Search filter
    if (searchTerm) {
      result = result.filter(item =>
        item.title_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.title_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.excerpt_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.content_ar?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      result = result.filter(item => item.category === selectedCategory);
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.published_date) - new Date(a.published_date);
      } else if (sortBy === 'oldest') {
        return new Date(a.published_date) - new Date(b.published_date);
      } else if (sortBy === 'title') {
        return (a.title_ar || '').localeCompare(b.title_ar || '', 'ar');
      }
      return 0;
    });

    setFilteredNews(result);
    setCurrentPage(1); // Reset to first page on search/filter/sort
  }, [searchTerm, selectedCategory, sortBy, news]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredNews.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredNews.length);
  const currentNews = filteredNews.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    const element = document.getElementById('news-grid-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 450, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (start > 2) {
        pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push('...');
      }

      pages.push(totalPages);
    }
    return pages;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEO 
        title="الأخبار والفعاليات"
        description="تابع آخر أخبار وفعاليات جامعة ابن النفيس للعلوم الطبية. آخر المستجدات، الأنشطة الطلابية، والإعلانات الأكاديمية."
        keywords="أخبار جامعة ابن النفيس, فعاليات الجامعة, أنشطة طلابية, إعلانات أكاديمية"
        url="https://uni-content-fixer.preview.emergentagent.com/news"
      />
      
      {/* Hero Section */}
      <PageHero 
        title="الأخبار والفعاليات"
        subtitle="تابع أحدث وأهم الأخبار، الفعاليات والمؤتمرات في جامعة ابن النفيس للعلوم الطبية"
        backgroundImage={getImageUrl(settings?.page_news_bg)}
      />

      {/* Search and Filter Section */}
      <section className="py-8 bg-white dark:bg-gray-900 border-b dark:border-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            {/* Search Box */}
            <div className="relative flex-1 w-full">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="ابحث في عناوين ومحتوى الأخبار..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-4 pr-12 py-3 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-[#0F388E] focus:border-transparent dark:bg-gray-800 dark:text-white transition-all shadow-sm"
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2 w-full lg:w-auto">
              <SlidersHorizontal className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="flex-1 lg:w-52 px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-[#0F388E] dark:bg-gray-800 dark:text-white font-medium"
              >
                <option value="all">جميع الفئات ({news.length})</option>
                {categories.filter(cat => cat !== 'all').map((category) => (
                  <option key={category} value={category}>
                    {category} ({news.filter(n => n.category === category).length})
                  </option>
                ))}
              </select>
            </div>

            {/* Sort Dropdown */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full lg:w-48 px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-[#0F388E] dark:bg-gray-800 dark:text-white font-medium"
            >
              <option value="newest">الأحدث أولاً</option>
              <option value="oldest">الأقدم أولاً</option>
              <option value="title">الترتيب الأبجدي</option>
            </select>
          </div>

          {/* Results Count & Current Slice */}
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800 pt-3">
            <div className="flex items-center gap-2">
              <Newspaper className="w-4 h-4 text-[#0F388E] dark:text-[#C9A040]" />
              <span>
                عرض الأخبار من <strong className="text-[#0F388E] dark:text-white">{filteredNews.length > 0 ? startIndex + 1 : 0}</strong> إلى <strong className="text-[#0F388E] dark:text-white">{endIndex}</strong> من إجمالي <strong className="text-[#C9A040] font-bold">{filteredNews.length}</strong> خبراً
              </span>
            </div>
            <div className="bg-blue-50 dark:bg-gray-800 text-[#0F388E] dark:text-blue-300 px-3 py-1 rounded-full text-xs font-semibold border border-blue-100 dark:border-gray-700">
              الصفحة {currentPage} من أصل {totalPages} (9 أخبار لكل صفحة)
            </div>
          </div>
        </div>
      </section>

      {/* News Grid Section */}
      <section id="news-grid-section" className="py-12 bg-gray-50/70 dark:bg-gray-900 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredNews.length === 0 ? (
            <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
              <Newspaper className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-300 text-lg font-medium mb-2">
                {searchTerm || selectedCategory !== 'all' 
                  ? 'لا توجد أخبار تطابق معايير البحث المحددة' 
                  : 'لا توجد أخبار منشورة حالياً'}
              </p>
              <Button
                variant="outline"
                onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}
                className="mt-2 text-[#0F388E] border-[#0F388E]"
              >
                إعادة ضبط الفلاتر
              </Button>
            </div>
          ) : (
            <>
              {/* Cards Grid: 10 items rendered beautifully in a 3-column layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {currentNews.map((item, idx) => (
                  <Link key={item.id} to={`/news/${item.id}`} className="block h-full group">
                    <Card className="h-full overflow-hidden hover:shadow-2xl transition-all duration-300 border border-gray-100 dark:border-gray-800 hover:border-[#C9A040] flex flex-col bg-white dark:bg-gray-800 rounded-2xl group-hover:-translate-y-1">
                      {/* Image Container */}
                      <div className="relative h-56 overflow-hidden bg-gray-100 dark:bg-gray-700">
                        <img
                          src={getImageUrl(item.image, 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=600')}
                          alt={item.title_ar}
                          className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-500"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/600x400?text=جامعة+ابن+النفيس';
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity"></div>
                        
                        {/* Category Badge */}
                        <div className="absolute top-4 right-4 bg-[#0F388E] text-white px-3.5 py-1.5 rounded-full text-xs font-bold shadow-md">
                          {item.category || 'أخبار'}
                        </div>

                        {/* Order Index Pill */}
                        <div className="absolute bottom-3 right-4 text-white/90 text-xs font-medium flex items-center gap-1.5 bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full">
                          <Calendar className="w-3.5 h-3.5 text-[#C9A040]" />
                          <span>
                            {new Date(item.published_date).toLocaleDateString('ar-EG', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                      
                      {/* Content */}
                      <CardContent className="p-6 flex-1 flex flex-col justify-between">
                        <div>
                          {/* Title */}
                          <h3 className="text-lg font-bold text-[#0F388E] dark:text-white mb-3 group-hover:text-[#C9A040] transition-colors line-clamp-2 leading-snug">
                            {item.title_ar}
                          </h3>
                          
                          {/* Excerpt */}
                          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3 leading-relaxed mb-4">
                            {item.excerpt_ar || item.content_ar?.replace(/<[^>]*>?/gm, '').substring(0, 120) + '...'}
                          </p>
                        </div>
                        
                        {/* Read More Link */}
                        <div className="pt-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between mt-auto">
                          <span className="text-[#C9A040] group-hover:text-[#0F388E] font-bold text-sm flex items-center gap-1.5 transition-colors">
                            <span>اقرأ التفاصيل</span>
                            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                          </span>
                          <span className="text-xs text-gray-400">
                            خبر #{startIndex + idx + 1}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>

              {/* Pagination Navigation Bar */}
              {totalPages > 1 && (
                <div className="mt-14 pt-8 border-t border-gray-200 dark:border-gray-800">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-5 bg-white dark:bg-gray-800 p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
                    {/* Summary Info */}
                    <div className="text-sm text-gray-600 dark:text-gray-300 font-medium text-center md:text-right">
                      عرض الأخبار <span className="font-bold text-[#0F388E] dark:text-[#C9A040]">{startIndex + 1}</span> - <span className="font-bold text-[#0F388E] dark:text-[#C9A040]">{endIndex}</span> من إجمالي <span className="font-bold text-[#0F388E] dark:text-[#C9A040]">{filteredNews.length}</span> خبراً
                    </div>

                    {/* Pagination Controls */}
                    <nav aria-label="شريط تنقل الأخبار" className="flex items-center gap-1.5 flex-wrap justify-center">
                      {/* First Page Button */}
                      <button
                        onClick={() => goToPage(1)}
                        disabled={currentPage === 1}
                        className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-gray-700 dark:text-gray-300"
                        title="الصفحة الأولى"
                      >
                        <ChevronsRight className="w-4 h-4" />
                      </button>

                      {/* Previous Page Button */}
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="flex items-center gap-1 px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-sm font-semibold text-gray-700 dark:text-gray-300"
                      >
                        <ChevronRight className="w-4 h-4" />
                        <span>السابق</span>
                      </button>

                      {/* Page Numbers */}
                      <div className="flex items-center gap-1 mx-1">
                        {getPageNumbers().map((pageNum, idx) => {
                          if (pageNum === '...') {
                            return (
                              <span key={`dots-${idx}`} className="px-2 py-1 text-gray-400 select-none text-sm">
                                ...
                              </span>
                            );
                          }
                          const isActive = pageNum === currentPage;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => goToPage(pageNum)}
                              className={`w-10 h-10 rounded-xl text-sm font-bold transition-all ${
                                isActive
                                  ? 'bg-[#0F388E] text-white shadow-md shadow-[#0F388E]/25 ring-2 ring-[#0F388E]/20 scale-105'
                                  : 'border border-gray-200 dark:border-gray-700 hover:bg-[#C9A040] hover:text-white hover:border-[#C9A040] text-gray-700 dark:text-gray-300'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      {/* Next Page Button */}
                      <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="flex items-center gap-1 px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-sm font-semibold text-gray-700 dark:text-gray-300"
                      >
                        <span>التالي</span>
                        <ChevronLeft className="w-4 h-4" />
                      </button>

                      {/* Last Page Button */}
                      <button
                        onClick={() => goToPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-gray-700 dark:text-gray-300"
                        title="الصفحة الأخيرة"
                      >
                        <ChevronsLeft className="w-4 h-4" />
                      </button>
                    </nav>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Announcements Section - After News List */}
          <div className="mt-20">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[#0F388E] dark:text-white mb-4">
                📢 الإعلانات والتعميمات الجامعية
              </h2>
              <div className="w-24 h-1 bg-[#C9A040] mx-auto"></div>
            </div>
            <Announcements location="news" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default News;
